import { Head, router } from '@inertiajs/react'
import { Trophy, TrendingUp, Medal, HelpCircle, CheckCircle2, XCircle, Flame, Award, Camera, Upload, Heart, Image as ImageIcon, Package, BarChart3 } from 'lucide-react'
import { MainLayout } from '@/components/layouts'
import { EditableContent } from '@/components/EditableContent'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { Textarea } from '@/components/ui/Textarea'
import type { EditableContent as EditableContentType } from '@/types'
import { useState, useRef, FormEvent } from 'react'

interface Answer {
  text: string
}

interface UserSubmission {
  selected_answer_indices: number[]
  is_correct: boolean
  points_earned: number
  streak_bonus_applied: boolean
  correct_answer_indices: number[]
}

interface CurrentQuiz {
  id: number
  title: string
  question: string
  answers: Answer[]
  category: string
  category_label: string
  difficulty_level: number
  points_reward: number
  week_range: string
  total_submissions: number
  success_rate: number
  multiple_choice: boolean
  correct_answer_count: number
  user_has_submitted: boolean
  user_submission: UserSubmission | null
}

interface UserQuizStats {
  total_quizzes: number
  correct_answers: number
  success_rate: number
  current_streak: number
  total_points: number
}

interface ContestSubmission {
  id: number
  user_name: string
  user_id: number
  image_url: string
  caption: string
  votes_count: number
  rank: number | null
  is_own_submission: boolean
  user_has_voted: boolean
  created_at: string
}

interface UserContestSubmission {
  id: number
  image_url: string
  caption: string
  votes_count: number
  status: string
  rank: number | null
  points_awarded: number
  created_at: string
}

interface CurrentContest {
  id: number
  title: string
  description: string
  theme: string
  status: string
  accepting_submissions: boolean
  accepting_votes: boolean
  submission_end_date: string
  voting_end_date: string
  participation_points: number
  vote_points: number
  user_has_submitted: boolean
  user_submission: UserContestSubmission | null
  submissions: ContestSubmission[]
  winners: ContestSubmission[]
}

interface QuizLeaderboardEntry {
  rank: number
  user_id: number
  name: string
  quiz_points: number
  correct_count: number
  quizzes_count: number
  success_rate: number
  is_current_user: boolean
}

interface QuizLeaderboard {
  entries: QuizLeaderboardEntry[]
  current_user_rank: number | null
}

interface TedChallenge {
  ted_count: number
  points_earned: number
  points_per_ted: number
}

interface SurveyResult {
  index: number
  text: string
  count: number
  percentage: number
}

interface UserSurveyResponse {
  selected_answer_indices: number[]
  points_earned: number
}

interface CurrentSurvey {
  id: number
  title: string
  question: string
  answers: Answer[]
  multiple_choice: boolean
  points_reward: number
  date_range: string
  total_responses: number
  user_has_responded: boolean
  user_response: UserSurveyResponse | null
  results: SurveyResult[]
}

interface ChallengesProps {
  editable_banner: EditableContentType | null
  current_survey: CurrentSurvey | null
  current_quiz: CurrentQuiz | null
  user_quiz_stats: UserQuizStats
  quiz_leaderboard: QuizLeaderboard
  current_contest: CurrentContest | null
  ted_challenge: TedChallenge | null
  is_pharmacien: boolean
}

const categoryColors: Record<string, string> = {
  sav: 'bg-tessan-cream text-tessan-green border border-tessan-green/20',
  discours: 'bg-tessan-cream text-tessan-green',
  pratiques: 'bg-tessan-green text-white'
}

interface PhotoContestSectionProps {
  contest: CurrentContest
}

function PhotoContestSection({ contest }: PhotoContestSectionProps) {
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [caption, setCaption] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showAllPhotos, setShowAllPhotos] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const PHOTOS_PREVIEW_COUNT = 3
  const hasMorePhotos = contest.submissions.length > PHOTOS_PREVIEW_COUNT
  const visibleSubmissions = showAllPhotos
    ? contest.submissions
    : contest.submissions.slice(0, PHOTOS_PREVIEW_COUNT)

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedImage(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmitPhoto = (e: FormEvent) => {
    e.preventDefault()
    if (!selectedImage) {
      alert('Veuillez sélectionner une photo')
      return
    }

    setIsSubmitting(true)
    const formData = new FormData()
    formData.append('image', selectedImage)
    formData.append('caption', caption)

    router.post(`/defis/contest/${contest.id}/submit`, formData, {
      forceFormData: true
    })
  }

  const handleVote = (submissionId: number) => {
    router.post(`/defis/contest/${contest.id}/vote/${submissionId}`)
  }

  const handleUnvote = (submissionId: number) => {
    router.delete(`/defis/contest/${contest.id}/vote/${submissionId}`)
  }

  return (
    <section className="bg-white rounded-xl border border-tessan-green/20">
      <div className="p-6">
        {/* Header - Title left, badge right */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-tessan-green flex items-center justify-center flex-shrink-0">
              <Camera className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-bold text-tessan-green">
                {contest.title || 'Concours Photo'}
              </h2>
              {contest.theme && (
                <p className="text-gray-500 text-sm">Thème : {contest.theme}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {contest.accepting_submissions && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-tessan-green text-white">
                📸 Soumissions ouvertes
              </span>
            )}
            {contest.accepting_votes && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-tessan-green text-white">
                🗳️ Votes en cours
              </span>
            )}
            {contest.status === 'winners_declared' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-tessan-green text-white">
                🏆 Gagnants annoncés
              </span>
            )}
          </div>
        </div>

        {contest.description && (
          <p className="text-gray-600 text-sm mb-5">{contest.description}</p>
        )}

        {/* User's Submission - Compact banner */}
        {contest.user_submission && (
          <div className="flex items-center gap-4 p-3 rounded-lg bg-gray-50 border border-gray-200 mb-5">
            <img
              src={contest.user_submission.image_url}
              alt="Votre photo"
              className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-tessan-green flex-shrink-0" />
                <span className="font-medium text-tessan-green text-sm">Votre participation</span>
                {contest.user_submission.caption && (
                  <span className="text-gray-400 text-sm truncate">&mdash; {contest.user_submission.caption}</span>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1">
                <span className="flex items-center gap-1 text-sm text-gray-600">
                  <Heart className="w-3.5 h-3.5 text-red-400" />
                  <strong>{contest.user_submission.votes_count}</strong> votes
                </span>
                {contest.user_submission.rank && (
                  <span className="text-sm font-medium text-tessan-green">
                    {contest.user_submission.rank === 1 && '🥇 1ère place'}
                    {contest.user_submission.rank === 2 && '🥈 2ème place'}
                    {contest.user_submission.rank === 3 && '🥉 3ème place'}
                  </span>
                )}
                {contest.user_submission.status === 'pending_review' && (
                  <span className="text-orange-600 text-sm">⏳ En attente de validation</span>
                )}
                {contest.user_submission.status === 'rejected' && (
                  <span className="text-red-600 text-sm">❌ Non approuvée</span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Submission Form */}
        {contest.accepting_submissions && !contest.user_has_submitted && (
          <form onSubmit={handleSubmitPhoto} className="mb-5">
            <div className="flex gap-4 items-start">
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                {!imagePreview ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-40 border-2 border-dashed border-tessan-green/30 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-tessan-green hover:bg-gray-50 transition-colors"
                  >
                    <Upload className="w-8 h-8 text-tessan-green/50" />
                    <span className="text-gray-500 text-sm font-medium">Choisir une photo</span>
                    <span className="text-xs text-gray-400">JPG, PNG, max 10MB</span>
                  </button>
                ) : (
                  <div className="relative">
                    <img src={imagePreview} alt="Preview" className="w-full h-40 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => { setImagePreview(null); setSelectedImage(null); if (fileInputRef.current) fileInputRef.current.value = '' }}
                      className="absolute top-2 right-2 bg-tessan-green/80 text-white rounded-full p-1.5 hover:bg-tessan-green"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex-1 flex flex-col gap-3">
                <Textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Légende (optionnelle)..."
                  maxLength={500}
                  rows={3}
                  className="text-sm"
                />
                <Button type="submit" disabled={!selectedImage || isSubmitting} className="w-full">
                  {isSubmitting ? 'Envoi...' : `Soumettre (+${contest.participation_points} pts)`}
                </Button>
              </div>
            </div>
          </form>
        )}

        {/* Winners Podium */}
        {contest.status === 'winners_declared' && contest.winners.length > 0 && (
          <div className="mb-5">
            <h3 className="font-heading text-lg font-bold text-tessan-green mb-4 flex items-center gap-2">
              <Award className="w-5 h-5" />
              Les gagnants
            </h3>
            <div className="grid grid-cols-3 gap-4">
              {contest.winners.map((winner) => (
                <div key={winner.id} className="relative group">
                  <div className="relative overflow-hidden rounded-xl">
                    <img
                      src={winner.image_url}
                      alt={`Photo de ${winner.user_name}`}
                      className="w-full aspect-square object-cover"
                    />
                    <div className="absolute top-2 left-2">
                      <span className="text-2xl">
                        {winner.rank === 1 && '🥇'}
                        {winner.rank === 2 && '🥈'}
                        {winner.rank === 3 && '🥉'}
                      </span>
                    </div>
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-3 pt-8">
                      <p className="font-medium text-white text-sm">{winner.user_name}</p>
                      <p className="text-white/80 text-xs flex items-center gap-1">
                        <Heart className="w-3 h-3" /> {winner.votes_count} votes
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photo Gallery */}
        {(contest.accepting_votes || contest.status === 'winners_declared') && contest.submissions.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg font-bold text-tessan-green">
                {contest.accepting_votes ? 'Votez pour vos photos préférées' : 'Toutes les participations'}
              </h3>
              <span className="text-sm text-gray-500">{contest.submissions.length} photo{contest.submissions.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="grid grid-cols-3 gap-5">
              {visibleSubmissions.map((submission) => (
                <div key={submission.id} className="group">
                  <div className="relative overflow-hidden rounded-xl border border-gray-200">
                    <img
                      src={submission.image_url}
                      alt={`Photo de ${submission.user_name}`}
                      className="w-full aspect-[4/3] object-cover"
                    />
                    {submission.is_own_submission && (
                      <div className="absolute top-2.5 right-2.5 bg-tessan-green text-white text-xs font-medium px-2.5 py-1 rounded-full">
                        Votre photo
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4 pt-10">
                      <p className="font-medium text-white text-sm">{submission.user_name}</p>
                      {submission.caption && (
                        <p className="text-white/70 text-xs mt-0.5 line-clamp-1">{submission.caption}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2.5">
                    <span className="text-sm text-gray-600 flex items-center gap-1.5">
                      <Heart className={`w-4 h-4 ${submission.user_has_voted ? 'fill-red-400 text-red-400' : 'text-gray-400'}`} />
                      <span>{submission.votes_count} vote{submission.votes_count !== 1 ? 's' : ''}</span>
                    </span>
                    {contest.accepting_votes && !submission.is_own_submission && (
                      <button
                        onClick={() => submission.user_has_voted ? handleUnvote(submission.id) : handleVote(submission.id)}
                        className={`text-sm font-medium px-4 py-1.5 rounded-full transition-colors ${
                          submission.user_has_voted
                            ? 'bg-tessan-green text-white'
                            : 'border border-tessan-green text-tessan-green hover:bg-tessan-green hover:text-white'
                        }`}
                      >
                        {submission.user_has_voted ? '✓ Voté' : 'Voter'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Show more / Show less */}
            {hasMorePhotos && (
              <div className="mt-5 text-center">
                <button
                  onClick={() => setShowAllPhotos(!showAllPhotos)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-tessan-green text-tessan-green text-sm font-medium hover:bg-tessan-green hover:text-white transition-colors"
                >
                  <ImageIcon className="w-4 h-4" />
                  {showAllPhotos
                    ? 'Voir moins'
                    : `Voir toutes les photos (${contest.submissions.length})`
                  }
                </button>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!contest.accepting_submissions && !contest.accepting_votes && contest.status !== 'winners_declared' && (
          <div className="text-center py-12 text-gray-500">
            <Camera className="w-12 h-12 mx-auto mb-3 text-tessan-green/30" />
            <p className="font-medium">Prochain concours bientôt !</p>
            <p className="text-sm mt-1">Restez connecté pour participer</p>
          </div>
        )}
      </div>
    </section>
  )
}

interface TedChallengeSectionProps {
  tedChallenge: TedChallenge
}

function TedChallengeSection({ tedChallenge }: TedChallengeSectionProps) {
  return (
    <section className="bg-white rounded-xl border border-tessan-green/20 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Package className="w-6 h-6 text-tessan-primary" />
          <h2 className="font-heading text-xl font-bold text-gray-900">
            Défi Télé expertise dermatologique
          </h2>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-tessan-cream rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-tessan-green">{tedChallenge.ted_count}</p>
          <p className="text-sm text-gray-600 mt-1">Téléconsultations réalisées</p>
        </div>
        <div className="bg-tessan-cream rounded-lg p-4 text-center">
          <p className="text-3xl font-bold text-tessan-green">{tedChallenge.points_earned}</p>
          <p className="text-sm text-gray-600 mt-1">Points gagnés</p>
        </div>
      </div>

      <p className="text-center text-sm text-gray-500 mt-4">
        +{tedChallenge.points_per_ted} points par télé expertise dermatologique réalisée
      </p>
    </section>
  )
}

interface SurveySectionProps {
  survey: CurrentSurvey
}

function SurveySection({ survey }: SurveySectionProps) {
  const [selectedSurveyAnswers, setSelectedSurveyAnswers] = useState<number[]>([])
  const [isSurveySubmitting, setIsSurveySubmitting] = useState(false)

  const toggleSurveyAnswer = (index: number) => {
    if (survey.multiple_choice) {
      setSelectedSurveyAnswers(prev =>
        prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
      )
    } else {
      setSelectedSurveyAnswers([index])
    }
  }

  const handleSubmitSurvey = () => {
    if (selectedSurveyAnswers.length === 0) return
    setIsSurveySubmitting(true)
    router.post(`/defis/survey/${survey.id}/submit`, {
      selected_answer_indices: selectedSurveyAnswers
    })
  }

  return (
    <section className="bg-white rounded-xl border border-tessan-green/20 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-tessan-green flex items-center justify-center flex-shrink-0">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-bold text-tessan-green">
              Sondage
            </h2>
            <p className="text-sm text-gray-500">{survey.date_range}</p>
          </div>
        </div>
        {survey.points_reward > 0 && !survey.user_has_responded && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-tessan-cream text-tessan-green border border-tessan-green/20">
            +{survey.points_reward} pts
          </span>
        )}
      </div>

      <h3 className="font-semibold text-gray-900 text-lg mb-4">{survey.question}</h3>

      {!survey.user_has_responded ? (
        /* Survey form */
        <div>
          {survey.multiple_choice && (
            <p className="text-sm text-blue-600 font-medium mb-3">
              Plusieurs réponses possibles
            </p>
          )}

          <div className="space-y-2 mb-4">
            {survey.answers.map((answer, index) => (
              <label
                key={index}
                className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedSurveyAnswers.includes(index)
                    ? 'border-tessan-green bg-tessan-cream'
                    : 'border-gray-200 hover:border-tessan-green/50 hover:bg-tessan-cream/50'
                }`}
              >
                <input
                  type={survey.multiple_choice ? 'checkbox' : 'radio'}
                  name="survey_answer"
                  checked={selectedSurveyAnswers.includes(index)}
                  onChange={() => toggleSurveyAnswer(index)}
                  className="text-tessan-primary w-5 h-5 accent-tessan-primary"
                />
                <span className="flex-1 text-gray-900">{answer.text}</span>
              </label>
            ))}
          </div>

          <Button
            onClick={handleSubmitSurvey}
            disabled={selectedSurveyAnswers.length === 0 || isSurveySubmitting}
            className="w-full"
          >
            {isSurveySubmitting ? 'Envoi...' : 'Voter'}
          </Button>

          <p className="mt-3 text-center text-sm text-gray-500">
            {survey.total_responses} participation{survey.total_responses !== 1 ? 's' : ''}
          </p>
        </div>
      ) : (
        /* Survey results */
        <div>
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle2 className="w-5 h-5 text-tessan-green" />
            <span className="font-semibold text-tessan-green">Merci pour votre vote !</span>
            {survey.user_response && survey.user_response.points_earned > 0 && (
              <span className="text-sm text-tessan-green ml-auto">+{survey.user_response.points_earned} pts</span>
            )}
          </div>

          <div className="space-y-3">
            {survey.results.map((result) => {
              const isUserAnswer = survey.user_response?.selected_answer_indices.includes(result.index)
              return (
                <div key={result.index}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm ${isUserAnswer ? 'font-semibold text-tessan-green' : 'text-gray-700'}`}>
                      {result.text}
                      {isUserAnswer && ' (votre choix)'}
                    </span>
                    <span className="text-sm font-medium text-gray-600">{result.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3">
                    <div
                      className={`rounded-full h-3 transition-all ${isUserAnswer ? 'bg-tessan-green' : 'bg-tessan-primary/60'}`}
                      style={{ width: `${result.percentage}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{result.count} vote{result.count !== 1 ? 's' : ''}</p>
                </div>
              )
            })}
          </div>

          <p className="mt-4 text-center text-sm text-gray-500">
            {survey.total_responses} participation{survey.total_responses !== 1 ? 's' : ''} au total
          </p>
        </div>
      )}
    </section>
  )
}

export default function Challenges({ editable_banner, current_survey, current_quiz, user_quiz_stats, quiz_leaderboard, current_contest, ted_challenge, is_pharmacien }: ChallengesProps) {
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const toggleAnswer = (index: number) => {
    setSelectedAnswers(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    )
  }

  const handleSubmitQuiz = () => {
    if (selectedAnswers.length === 0) {
      alert('Veuillez sélectionner au moins une réponse')
      return
    }

    setIsSubmitting(true)
    router.post(`/defis/quiz/${current_quiz!.id}/submit`, {
      selected_answer_indices: selectedAnswers
    })
  }

  return (
    <MainLayout
      title="Défis"
    >
      <Head title="Défis" />

      {/* Banner éditable (si existe) */}
      {editable_banner && (
        <EditableContent
          content={editable_banner}
          wrapperClassName="mb-8 -mt-8 -mx-4"
        />
      )}

      <div>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Trophy className="w-8 h-8 text-tessan-primary" />
            <h1 className="font-heading text-3xl font-bold text-gray-900">
              Défis
            </h1>
          </div>
          <p className="text-gray-500">
            Relevez les défis pour gagner des points et gravir le classement !
          </p>
        </div>

        {/* Sondage - Full width at top */}
        {current_survey && (
          <SurveySection survey={current_survey} />
        )}

        {/* Grid layout: 2 columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column: Challenges */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quiz Hebdomadaire */}
            <section className="bg-white rounded-xl border border-tessan-green/20 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <HelpCircle className="w-6 h-6 text-tessan-primary" />
                  <h2 className="font-heading text-xl font-bold text-gray-900">
                    Quiz Hebdomadaire
                  </h2>
                </div>
                {current_quiz && (
                  <Badge className={categoryColors[current_quiz.category]}>
                    {current_quiz.category_label}
                  </Badge>
                )}
              </div>

              {current_quiz ? (
                <>
                  {!current_quiz.user_has_submitted ? (
                    // Quiz non répondu
                    <div>
                      <div className="bg-white rounded-lg p-5 mb-4">
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="font-semibold text-gray-900 text-lg flex-1">
                            {current_quiz.question}
                          </h3>
                        </div>

                        {current_quiz.multiple_choice && (
                          <p className="text-sm text-blue-600 font-medium mb-3">
                            Plusieurs réponses possibles ({current_quiz.correct_answer_count} bonnes réponses)
                          </p>
                        )}

                        <div className="space-y-2 mt-4">
                          {current_quiz.answers.map((answer, index) => (
                            <label
                              key={index}
                              className={`flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                selectedAnswers.includes(index)
                                  ? 'border-tessan-green bg-tessan-cream'
                                  : 'border-gray-200 hover:border-tessan-green/50 hover:bg-tessan-cream/50'
                              }`}
                            >
                              <input
                                type={current_quiz.multiple_choice ? 'checkbox' : 'radio'}
                                name="quiz_answer"
                                checked={selectedAnswers.includes(index)}
                                onChange={() => {
                                  if (current_quiz.multiple_choice) {
                                    toggleAnswer(index)
                                  } else {
                                    setSelectedAnswers([index])
                                  }
                                }}
                                className="text-tessan-primary w-5 h-5 accent-tessan-primary"
                              />
                              <span className="flex-1 text-gray-900">{answer.text}</span>
                            </label>
                          ))}
                        </div>

                        <div className="mt-4 flex items-center justify-between">
                          <p className="text-sm text-gray-600">
                            <strong>+{current_quiz.points_reward} points</strong> si correct
                            {user_quiz_stats.current_streak >= 2 && (
                              <span className="ml-2 text-orange-600">
                                (+10 bonus si {3 - (user_quiz_stats.current_streak % 3)} bonne(s) réponse(s) de plus 🔥)
                              </span>
                            )}
                          </p>
                        </div>
                      </div>

                      <Button
                        onClick={handleSubmitQuiz}
                        disabled={selectedAnswers.length === 0 || isSubmitting}
                        className="w-full"
                      >
                        {isSubmitting ? 'Envoi...' : 'Valider ma réponse'}
                      </Button>

                      <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
                        <span>{current_quiz.total_submissions} participation{current_quiz.total_submissions > 1 ? 's' : ''}</span>
                        <span>Taux de réussite: {current_quiz.success_rate}%</span>
                      </div>
                    </div>
                  ) : (
                    // Quiz déjà répondu
                    <div>
                      <div className="bg-white rounded-lg p-5 mb-4">
                        <div className="flex items-center gap-2 mb-4">
                          <CheckCircle2 className="w-5 h-5 text-tessan-green" />
                          <h3 className="font-semibold text-tessan-green">Quiz complété</h3>
                        </div>

                        <div className="space-y-3">
                          {current_quiz.answers.map((answer, index) => {
                            const isUserAnswer = current_quiz.user_submission!.selected_answer_indices.includes(index)
                            const isCorrectAnswer = current_quiz.user_submission!.correct_answer_indices.includes(index)

                            return (
                              <div
                                key={index}
                                className={`flex items-center gap-3 p-3 rounded-lg border-2 ${
                                  isCorrectAnswer
                                    ? 'bg-tessan-cream border-tessan-green'
                                    : isUserAnswer
                                    ? 'bg-tessan-cream/60 border-tessan-green/30'
                                    : 'bg-gray-50 border-gray-200'
                                }`}
                              >
                                {isCorrectAnswer ? (
                                  <CheckCircle2 size={20} className="text-tessan-green flex-shrink-0" />
                                ) : isUserAnswer ? (
                                  <XCircle size={20} className="text-tessan-green/40 flex-shrink-0" />
                                ) : (
                                  <div className="w-5 h-5" />
                                )}
                                <span className="flex-1 text-gray-900">{answer.text}</span>
                                {isCorrectAnswer && (
                                  <Badge className="bg-tessan-green text-white">Bonne réponse</Badge>
                                )}
                                {isUserAnswer && !isCorrectAnswer && (
                                  <Badge className="bg-tessan-cream text-tessan-green border border-tessan-green/20">Votre réponse</Badge>
                                )}
                              </div>
                            )
                          })}
                        </div>

                        <div className={`mt-4 p-3 rounded-lg ${
                          current_quiz.user_submission!.is_correct ? 'bg-tessan-cream border border-tessan-green/20' : 'bg-tessan-cream/60 border border-tessan-green/10'
                        }`}>
                          {current_quiz.user_submission!.is_correct ? (
                            <div className="flex items-center gap-2">
                              <Trophy className="w-5 h-5 text-tessan-green" />
                              <p className="text-tessan-green font-medium">
                                Bravo ! +{current_quiz.user_submission!.points_earned} points
                                {current_quiz.user_submission!.streak_bonus_applied && (
                                  <span className="ml-2">🔥 Bonus streak !</span>
                                )}
                              </p>
                            </div>
                          ) : (
                            <p className="text-tessan-green">
                              Dommage, ce n'était pas la bonne réponse. Réessayez la semaine prochaine !
                            </p>
                          )}
                        </div>
                      </div>

                      <p className="text-center text-gray-500 text-sm">
                        Revenez lundi prochain pour un nouveau quiz !
                      </p>
                    </div>
                  )}
                </>
              ) : (
                // Pas de quiz cette semaine
                <div className="text-center py-8 text-gray-600">
                  <HelpCircle className="w-12 h-12 mx-auto mb-3 text-tessan-primary/30" />
                  <p className="font-medium">Aucun quiz cette semaine</p>
                  <p className="text-sm mt-1">Le prochain quiz sera disponible prochainement !</p>
                </div>
              )}
            </section>

            {/* Défi TED - uniquement pour les pharmaciens */}
            {is_pharmacien && ted_challenge && (
              <TedChallengeSection tedChallenge={ted_challenge} />
            )}

            {/* Photo Contest - dans la colonne gauche */}
            {current_contest && (
              <PhotoContestSection contest={current_contest} />
            )}

          </div>

          {/* Right column: Stats & Leaderboard */}
          <div className="space-y-6">
            {/* Mes stats Quiz */}
            <section className="bg-white rounded-xl border border-tessan-green/20 p-6">
              <div className="flex items-center gap-3 mb-4">
                <TrendingUp className="w-6 h-6 text-tessan-green" />
                <h2 className="font-heading text-lg font-bold text-tessan-green">
                  Stats Quiz
                </h2>
              </div>
              <div className="space-y-4">
                {/* Streak */}
                {user_quiz_stats.current_streak >= 3 && (
                  <div className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Flame className="w-5 h-5 text-orange-600" />
                      <p className="text-sm font-medium text-orange-900">Streak actuel</p>
                    </div>
                    <p className="text-3xl font-bold text-orange-600">{user_quiz_stats.current_streak}🔥</p>
                  </div>
                )}

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Quiz répondus</p>
                    <p className="text-xl font-bold text-gray-900">{user_quiz_stats.total_quizzes}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Bonnes réponses</p>
                    <p className="text-xl font-bold text-tessan-green">{user_quiz_stats.correct_answers}</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Taux réussite</p>
                    <p className="text-xl font-bold text-tessan-primary">{user_quiz_stats.success_rate}%</p>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <p className="text-xs text-gray-600 mb-1">Points quiz</p>
                    <p className="text-xl font-bold text-tessan-green">{user_quiz_stats.total_points}</p>
                  </div>
                </div>

                {user_quiz_stats.current_streak > 0 && user_quiz_stats.current_streak < 3 && (
                  <div className="text-center p-3 bg-tessan-cream rounded-lg border border-tessan-green/10">
                    <p className="text-sm text-tessan-green font-medium">
                      Encore <strong>{3 - (user_quiz_stats.current_streak % 3)}</strong> bonne(s) réponse(s) pour un bonus ! 💪
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Classement Quiz */}
            <section className="bg-white rounded-xl border border-tessan-green/20 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Medal className="w-6 h-6 text-tessan-yellow-cta" />
                <h2 className="font-heading text-lg font-bold text-tessan-green">
                  Classement Quiz
                </h2>
              </div>

              {quiz_leaderboard.entries.length > 0 ? (
                <div className="space-y-2">
                  {quiz_leaderboard.entries.map((entry) => (
                    <div
                      key={entry.user_id}
                      className={`flex items-center gap-3 p-2.5 rounded-lg transition-colors ${
                        entry.is_current_user
                          ? 'bg-tessan-cream border border-tessan-primary/20'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Rank */}
                      <div className="flex-shrink-0 w-6 flex justify-center">
                        {entry.rank === 1 ? (
                          <Trophy className="w-5 h-5 text-tessan-yellow-cta" />
                        ) : entry.rank === 2 ? (
                          <Medal className="w-5 h-5 text-gray-400" />
                        ) : entry.rank === 3 ? (
                          <Medal className="w-5 h-5 text-amber-600" />
                        ) : (
                          <span className="w-5 text-center text-gray-500 font-medium text-sm">{entry.rank}</span>
                        )}
                      </div>

                      {/* Avatar */}
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-tessan-primary text-white flex items-center justify-center text-xs font-medium">
                        {entry.name
                          .split(' ')
                          .map((p) => p[0])
                          .join('')
                          .toUpperCase()
                          .slice(0, 2)}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className={`font-medium text-sm truncate ${entry.is_current_user ? 'text-tessan-primary' : 'text-gray-900'}`}>
                            {entry.name}
                          </span>
                          {entry.is_current_user && (
                            <span className="text-xs text-tessan-primary">(vous)</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">
                          {entry.correct_count}/{entry.quizzes_count} ({entry.success_rate}%)
                        </p>
                      </div>

                      {/* Points */}
                      <div className="flex-shrink-0 text-right">
                        <span className="font-semibold text-tessan-primary text-sm">{entry.quiz_points}</span>
                        <span className="text-xs text-gray-500 ml-0.5">pts</span>
                      </div>
                    </div>
                  ))}

                  {/* Position de l'utilisateur si hors top 10 */}
                  {quiz_leaderboard.current_user_rank && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-sm text-gray-600 text-center">
                        Votre position : <span className="font-semibold text-tessan-primary">{quiz_leaderboard.current_user_rank}e</span>
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <Medal className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p className="text-sm">Aucun participant pour le moment</p>
                  <p className="text-xs mt-1">Répondez au quiz pour apparaitre ici !</p>
                </div>
              )}
            </section>
          </div>
        </div>

      </div>
    </MainLayout>
  )
}
